<template>
  <v-card flat>
    <slot name="header">
      <v-card-title v-if="!hideHeader">
        <v-row align="center">
          <v-col cols="12" md="auto">
            <slot name="title">Class Schedules</slot>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" md="auto" class="text-right">
            <v-row align="center" justify="end">
              <v-col v-if="!readonly && showAdd" cols="12" md="auto">
                <schedule-form-dialog
                  v-model="addDialog"
                  :classroom-id="classroom.id"
                  :lesson-plan="subscriptionPackage.lesson_plan"
                  submit-api
                  @submit="onSubmitAddSchedule"
                >
                  <template #activator="{ on, attrs }">
                    <v-btn
                      color="success"
                      depressed
                      :block="$vuetify.breakpoint.mobile"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon left>mdi-plus</v-icon>Tambah
                    </v-btn>
                  </template>
                </schedule-form-dialog>
              </v-col>
              <v-col v-if="!readonly && showGenerate" cols="12" md="auto">
                <v-tooltip top>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      depressed
                      color="info"
                      v-bind="attrs"
                      :block="$vuetify.breakpoint.mobile"
                      v-on="on"
                      @click="generateDialog = true"
                    >
                      <v-icon left>mdi-refresh</v-icon>Generate Ulang
                    </v-btn>
                  </template>
                  <span>
                    Generate ulang jadwal-jadwal kelas yang belum selesai.
                    Jadwal-jadwal kelas yang sudah selesai tidak akan terdampak
                  </span>
                </v-tooltip>
              </v-col>
              <v-col cols="12" md="auto">
                <v-btn-toggle
                  v-model="selectedTab"
                  :block="$vuetify.breakpoint.mobile"
                >
                  <v-btn v-for="(t, j) in tabs" :key="j">
                    <v-icon left>{{ t.icon }}</v-icon>
                    {{ t.text }}
                  </v-btn>
                </v-btn-toggle>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-title>
      <v-divider v-if="!hideHeader"></v-divider>
    </slot>
    <v-card-text>
      <!-- Filter Section -->
      <v-row align="center">
        <v-col cols="12" md="2">
          <div class="label mb-2">Term</div>
          <v-select
            v-model="filters.term"
            :items="terms"
            outlined
            solo
            flat
            background-color="white"
            label="Term"
            hide-details
            dense
            clearable
            @change="getListData"
            @click:clear="getListData"
          />
        </v-col>
        <v-col cols="12" md="2">
          <div class="label mb-2">Month</div>
          <v-select
            v-model="filters.month"
            :items="months"
            outlined
            solo
            flat
            background-color="white"
            label="Month"
            hide-details
            dense
            clearable
            @change="getListData"
            @click:clear="getListData"
          />
        </v-col>
        <v-col cols="12" md="2">
          <div class="label mb-2">Session</div>
          <v-select
            v-model="filters.session"
            :items="sessions"
            outlined
            solo
            flat
            background-color="white"
            label="Session"
            hide-details
            dense
            clearable
            @change="getListData"
            @click:clear="getListData"
          />
        </v-col>
        <v-col md="2" cols="12">
          <div class="label mb-2">Status</div>
          <v-select
            v-model="filters.status"
            :items="statusOptions"
            outlined
            solo
            flat
            background-color="white"
            label="Status"
            hide-details
            dense
            clearable
            @change="getListData"
            @click:clear="getListData"
          ></v-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-slide-x-transition>
            <schedule-calendar
              v-show="selectedTab === tabView.calendar"
              :schedules="items"
              @click:event="editSchedule"
            ></schedule-calendar>
          </v-slide-x-transition>
          <v-slide-x-transition>
            <v-card flat>
              <v-card-text>
                <v-data-table
                  v-show="selectedTab === tabView.list"
                  :headers="headers"
                  :items="items"
                  :loading="isLoading"
                  :options.sync="options"
                  :server-items-length="
                    itemList.meta ? itemList.meta.total : items.length
                  "
                  item-key="id"
                  :footer-props="defaultDataTableFooterProps"
                >
                  <template #[`item.class_date`]="{ item }">
                    {{ formatDate(item.class_date, 'DD MMM YYYY') }}
                  </template>
                  <template #[`item.class_time`]="{ item }">
                    {{ formatTime(item.start_time) }} -
                    {{ formatTime(item.end_time) }}
                  </template>
                  <template #[`item.lessons`]="{ item }">
                    <ul v-if="item.lessons.length > 0">
                      <li v-for="(lesson, i) in item.lessons" :key="i">
                        {{ lesson.name }}
                      </li>
                    </ul>
                    <div v-else>No lessons</div>
                  </template>
                  <template #[`item.status`]="{ item }">
                    <v-chip :color="getStatusColor(item)" small dark>
                      <strong>{{ getStatusText(item) }}</strong>
                    </v-chip>
                  </template>
                  <template #[`item.action`]="{ item }">
                    <div class="text-right">
                      <v-btn
                        depressed
                        color="error"
                        small
                        :disabled="readonly"
                        @click="removeSchedule(item)"
                      >
                        <v-icon left>mdi-delete-forever</v-icon>Delete
                      </v-btn>
                      <v-btn
                        depressed
                        color="warning"
                        small
                        :disabled="readonly"
                        @click="editSchedule(item)"
                      >
                        <v-icon left>mdi-pencil</v-icon>Edit
                      </v-btn>
                    </div>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-slide-x-transition>
        </v-col>
      </v-row>
    </v-card-text>
    <schedule-form-dialog
      v-if="editDialog.show"
      v-model="editDialog.show"
      :item.sync="editDialog.item"
      :lesson-plan="subscriptionPackage.lesson_plan"
      submit-api
      @submit="onSubmitEditSchedule"
    ></schedule-form-dialog>
    <generate-schedule-dialog
      v-if="generateDialog"
      v-model="generateDialog"
      :classroom="classroom"
      :default-end-date="classroom.end_date"
      @confirm="onSubmitGenerateSchedule"
    ></generate-schedule-dialog>
  </v-card>
</template>
<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { ListParams } from '@/models'
import utils from '@/utils'
import ScheduleCalendar from '@components/ClassroomSchedule/CalendarView'
import ScheduleFormDialog from '@components/Classroom/ScheduleFormDialog'
import GenerateScheduleDialog from '@components/Classroom/GenerateScheduleDialog'

export const TAB_LIST_VIEW = 0
export const TAB_CALENDAR_VIEW = 1

export default {
  name: 'ClassroomScheduleList',
  components: {
    ScheduleCalendar,
    ScheduleFormDialog,
    GenerateScheduleDialog,
  },
  props: {
    classroom: {
      type: Object,
      default: () => null,
      required: false,
    },
    tab: {
      type: Number,
      default: TAB_CALENDAR_VIEW,
      required: false,
    },
    readonly: {
      type: Boolean,
      default: false,
      required: false,
    },
    hideHeader: {
      type: Boolean,
      default: false,
      required: false,
    },
    showGenerate: {
      type: Boolean,
      default: false,
      required: false,
    },
    showAdd: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      tabView: {
        list: TAB_LIST_VIEW,
        calendar: TAB_CALENDAR_VIEW,
      },
      filters: {
        classroom_id: null,
        term: null,
        month: null,
        session: null,
        status: null,
      },
      headers: [
        { text: 'Class Date', value: 'class_date' },
        { text: 'Class Time', value: 'class_time', sortable: false },
        { text: 'Term', value: 'term', sortable: false, align: 'center' },
        { text: 'Month', value: 'month', sortable: false, align: 'center' },
        { text: 'Session', value: 'session', sortable: false, align: 'center' },
        {
          text: 'Lessons',
          value: 'lessons',
          sortable: false,
          class: 'text-center',
        },
        {
          text: 'Status',
          value: 'status',
          sortable: false,
          class: 'text-center',
        },
        {
          text: 'Action',
          value: 'action',
          sortable: false,
          align: 'center',
        },
      ],
      options: {
        sortBy: ['class_date'],
        sortDesc: [false],
        page: 1,
        itemsPerPage: 10,
      },
      editDialog: {
        show: false,
        item: null,
        index: -1,
      },
      generateDialog: false,
      addDialog: false,
      selectedTab: 0,
      tabs: [
        {
          text: 'List View',
          icon: 'mdi-view-list',
          view: 'list',
        },
        {
          text: 'Calendar View',
          icon: 'mdi-calendar-month',
          view: 'calendar',
        },
      ],
      items: [],
    }
  },
  computed: {
    ...mapGetters({
      itemList: 'classroom/scheduleList',
      isItemListLoading: 'classroom/isScheduleListLoading',
      listParams: 'classroom/scheduleListParams',
      subscriptionPackage: 'subscriptionPackage/detail',
      studyProgressList: 'studyProgress/list',
      isStudyProgressListLoading: 'studyProgress/isListLoading',
    }),
    statusOptions() {
      return ['All', 'Completed', 'Pending Report']
    },
    studyProgresses() {
      return this.studyProgressList.data
    },
    groupedStudyProgressLessons() {
      return this.studyProgresses.reduce((carry, sp) => {
        if (!carry[sp.classroom_schedule_id]) {
          carry[sp.classroom_schedule_id] = []
        }
        if (sp.details.length > 0) {
          carry[sp.classroom_schedule_id] = sp.details.map((detail) => ({
            id: detail.lesson_id,
            name: detail.lesson_name,
            name_cn: detail.lesson_name_cn,
            lesson_category_id: detail.lesson_category_id,
          }))
        }
        return carry
      }, {})
    },
    isLoading() {
      return this.isItemListLoading || this.isStudyProgressListLoading
    },
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.getListData()
      },
    },
    selectedTab() {
      this.getListData()
    },
    'classroom.subscription_package_id'() {
      this.getSubscriptionPackage(this.classroom.subscription_package_id)
    },
  },
  created() {
    this.init()
  },
  methods: {
    ...mapActions({
      getItemList: 'classroom/getScheduleList',
      deleteSchedule: 'classroomSchedule/delete',
      getSubscriptionPackage: 'subscriptionPackage/getDetail',
      updateClassroom: 'classroom/update',
      generateSchedules: 'classroom/generateSchedules',
      getStudyProgressList: 'studyProgress/getList',
    }),
    ...mapMutations({
      setListParams: 'classroom/setScheduleListParams',
    }),
    async init() {
      if (this.classroom) {
        this.filters.classroom_id = this.classroom.id
        await this.getSubscriptionPackage(
          this.classroom.subscription_package_id
        )
      }
      this.selectedTab = this.tab
      await this.getListData()
    },
    async getListData() {
      const filters = _.cloneDeep(this.filters)
      filters.status = undefined
      switch (this.filters.status) {
        case 'Completed':
          filters.is_completed = 1
          break
        case 'Pending Report':
          filters.is_completed = 0
          break
        default:
          filters.is_completed = undefined
          break
      }
      if (this.selectedTab === TAB_LIST_VIEW) {
        const listParams = new ListParams(filters, this.options)
        await this.getItemList(utils.toServerSideQuery(listParams))
        this.setListParams(listParams)
      } else if (this.selectedTab === TAB_CALENDAR_VIEW) {
        await this.getItemList({
          ...filters,
          sort: ['class_date,asc'],
        })
      }
      await this.getStudyProgressList({
        classroom_id: [
          ...new Set(this.itemList.data.map((e) => e.classroom_id)),
        ],
      })
      this.items = this.itemList.data.map((e) => {
        const item = _.cloneDeep(e)
        if (item.lessons.length === 0) {
          item.lessons = this.groupedStudyProgressLessons[item.id] ?? []
        }
        return item
      })
    },
    getStatusColor(item) {
      const today = moment()
      const dt = moment(item.class_date)
      if (item.is_completed) {
        return 'success'
      } else if (dt.isSameOrBefore(today)) {
        return 'error'
      } else {
        return 'grey'
      }
    },
    getStatusText(item) {
      const today = moment()
      const dt = moment(item.class_date)
      if (item.is_completed) {
        return 'Completed'
      } else if (dt.isSameOrBefore(today)) {
        return 'Pending Report'
      } else {
        return 'Upcoming'
      }
    },
    async removeSchedule(item) {
      await this.deleteSchedule(item.id)
      await this.getListData()
    },
    editSchedule(item) {
      const schedule = this.itemList.data.find((e) => e.id === item.id)
      if (schedule) {
        this.editDialog.item = _.cloneDeep(schedule)
        this.editDialog.show = true
      }
    },
    async onSubmitAddSchedule() {
      this.addDialog = false
      await this.getListData()
    },
    async onSubmitEditSchedule() {
      await this.getListData()
      this.editDialog.index = -1
      this.editDialog.item = null
      this.editDialog.show = false
    },
    async onSubmitGenerateSchedule({ startDate, endDate }) {
      await this.updateClassroom(this.classroom)
      await this.generateSchedules({
        id: this.classroom.id,
        start_date: startDate,
        end_date: endDate,
      })
      await this.getListData()
      this.generateDialog = false
      this.$emit('generated', true)
    },
  },
}
</script>
